import React, { useEffect, useState } from 'react';
import { Worker, WorkerForm, SetLoading } from '../SiteClasses';
import { Modal, Field, Input, Button, Toast, Message, SegmentControl } from 'react-frontier';
import { bindFormChange, capitalizeName } from '../SiteUtil';
import SearchCountryCityModal from './SearchCountryCityModal';
import Validator from '../Validator';
import moment from 'moment';
import API from '../API';

interface ModalProps {
	open: boolean,
	onClose: ()=>void,
	onCreate?: (worker: Worker)=>void,
	onUpdate?: (worker: Worker)=>void,
	workerToEdit?: Worker,
}

var WorkerModal = (props: ModalProps) => {
	var isEditing = !!props.workerToEdit;
	var [form, setForm] = useState<WorkerForm>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [locationOption, setLocationOption] = useState<number>(1);
	var [searchModal, setSearchModal] = useState<boolean>(false);

	useEffect(() => {
		if(props.open){
			if(isEditing && props.workerToEdit){
				setForm({
					first_name: capitalizeName(props.workerToEdit.first_name),
					last_name: capitalizeName(props.workerToEdit.last_name),
					email: props.workerToEdit.email,
					phone: props.workerToEdit.phone,
					rfc: props.workerToEdit.rfc,
					curp: props.workerToEdit.curp,
					zipcode: props.workerToEdit.zipcode,
					comments: props.workerToEdit.comments || '',
					city_name: '',
				});
			}else{
				setForm({
					first_name: '',
					last_name: '',
					email: '',
					phone: '',
					rfc: '',
					curp: '',
					zipcode: '',
					comments: '',
					city_name: ''
				});
			}
		}
	}, [props.open, props.workerToEdit]);

	var submit = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator(form, {
			first_name: [{ rule: 'length', params: [3, 32], label: 'Nombre' }],
			last_name: [{ rule: 'length', params: [3, 64], label: 'Apellido' }],
			phone: [{ rule: 'phone', label: 'Teléfono' }],
			email: [{ rule: 'email', skipEmpty: true, label: 'Coreo eléctronico' }],
			curp: [{ label: 'CURP', rule: /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/gi }],
			zipcode: [{ rule: 'empty', label: 'Código Postal' }],
			rfc: [{ rule: 'minLength', params: [12], prompt: 'El RFC no es válido' }],
		});
		setModalPrompts(prompts);
		if (!valid) return;
		setLoading(true);
		
		if(isEditing && props.workerToEdit){
			API.editWorker(props.workerToEdit.worker_id, form).then(res=>{
				if (res.error) return setModalPrompts([res.message]);
				Toast.success('Se ha editado la información del trabajador');
				var updatedWorker: Worker = {
					...props.workerToEdit,
					first_name: capitalizeName(form.first_name),
					last_name: capitalizeName(form.last_name),
					email: form.email,
					phone: form.phone,
					rfc: form.rfc,
					curp: form.curp,
					zipcode: form.zipcode,
					comments: form.comments || null,
				};
				props.onUpdate?.(updatedWorker);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado actualizando al trabajador. (LCL-2)']);
			}).finally(()=>{
				setLoading(false);
			});
		} else {
			API.createWorker(form).then(res=>{
				if (res.error) return setModalPrompts([res.message]);
				Toast.success('Se ha creado el trabajador');
				var newWorker: Worker = {
					worker_id: res.data.worker_id,
					first_name: capitalizeName(form.first_name),
					last_name: capitalizeName(form.last_name),
					email: form.email,
					phone: form.phone,
					rfc: form.rfc,
					curp: form.curp,
					zipcode: form.zipcode,
					valid_rfc: false,
					deleted: false,
					user_creator: null,
					user_creator_name: null,
					user_deleted: null,
					user_deleted_name: null,
					date_created: moment().unix(),
					date_deleted: null,
					account_id: null,
					account: null,
				};
				props.onCreate?.(newWorker);
				props.onClose();
			}).catch(err=>{
				setModalPrompts(['Hubo un error inesperado actualizando al trabajador. (LCL-1)']);
			}).finally(()=>{
				setLoading(false);
			});
		}
	}

	var onFormChange = bindFormChange(form, setForm);

	return <>
		<Modal open={props.open} onClose={props.onClose} size='tiny'>
			<Modal.Header>{isEditing ? 'Editar trabajador' : 'Crear trabajador'}</Modal.Header>
			{form && <Modal.Content>
				<Field amount={2}>
					<Input 
						label='Nombre' 
						placeholder='Nombre de trabajador' 
						value={form.first_name}
						onChange={onFormChange('first_name')}
					/>
					<Input 
						label='Apellido' 
						value={form.last_name}
						onChange={onFormChange('last_name')}
					/>
				</Field>
				<Field amount={2}>
					<Input 
						label='Correo eletronico' 
						value={form.email}
						onChange={onFormChange('email')}
					/>
					<Input 
						label='Telefono' 
						value={form.phone}
						onChange={onFormChange('phone')}
					/>
				</Field>
				<Input 
					label='CURP' 
					value={form.curp}
					onChange={onFormChange('curp')}
				/>
				<Input 
					label='RFC' 
					value={form.rfc}
					onChange={onFormChange('rfc')}
				/>
				<Field label='Ubicación' style={{ marginTop: 15 }}>
					<SegmentControl
						color='blue'
						segments={[
							{ text: 'Codigo postal', value: 1 },
							{ text: 'Ciudad', value: 2 }
						]}
						onClick={setLocationOption}
						selected={locationOption ? [locationOption] : []}
					/>
				</Field>
				{locationOption === 1 ? (
					<Input
						label='Código Postal'
						value={form.zipcode}
						placeholder='Código postal'
						onChange={onFormChange('zipcode')}
					/>
				) : (
					<Field label='Ciudad' style={{ marginTop: 10 }}>
						<Input 
							value={form.city_name}
							placeholder='Selecciona una ciudad...'
							readonly
							button={
								<Button
									text='Seleccionar'
									onClick={() => setSearchModal(true)}
								/>
							}
						/>
					</Field>
				)}
				<Input
					label='Comentarios'
					value={form.comments || ''}
					onChange={onFormChange('comments')}
					placeholder='Comentarios'
					style={{ marginTop: 10 }}
					textarea
				/>
				<Message style={{ marginTop: 15 }} type='error' list={modalPrompts} />
			</Modal.Content>}
			<Modal.Actions>
				<Button basic text='Cerrar' onClick={props.onClose} />
				<Button color="black" text={isEditing ? 'Guardar' : 'Crear'} onClick={submit}/>
			</Modal.Actions>
		</Modal>
		<SearchCountryCityModal
			open={searchModal}
			onClose={() => setSearchModal(false)}
			onSelected={(c) => {
				setForm({ ...form, zipcode: c.zipcode, city_name: c.city_name });
				setSearchModal(false);
			}}
		/>
	</>
}

export default WorkerModal;